import React, {useEffect, useRef, useState} from 'react';
import {CircularProgress, Stack, TextField} from '@mui/material';
import {
  Modal,
  Button,
  Headline,
  Body,
  Label,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {ACCEPTED_MESSAGE_FILE_TYPES} from '@/config/conversationAttachments';
import CheckboxLabel from '@/components/checkbox-label';
import FlashMessage from '@/components/flash-message';
import MarkdownFormattingHint from '@/components/markdown-formatting-hint';
import StackedAvatars from '@/components/message-creators-modal/StackedAvatars';
import {MessageCreator} from '@/components/message-creators-modal/types';
import useBulkCreateMessages from '@/hooks/mutations/campaigns/use-bulk-create-messages';
import useMessageAttachments from '@/hooks/use-message-attachments';
import {BULK_MESSAGE_MODAL_SX} from './constants';

const BODY_PLACEHOLDER_KEY =
  'e.g. Hi! Just checking in about your deliverables for the campaign...';

type BulkMessageModalProps = {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  brandId: string;
  /** Paid gig ids of the selected (loaded) creators — sent as `contract_ids`. */
  contractIds: string[];
  /** Selected creators, used for the avatar stack. */
  creators: MessageCreator[];
  /**
   * Total accepted creators on the campaign (may exceed the loaded/selected
   * rows). Used to offer "send to all accepted" when the whole loaded page is
   * selected.
   */
  totalAcceptedCount: number;
  /** True when every loaded row is selected (the header "select all" state). */
  allLoadedSelected: boolean;
  /** Called after a successful send (e.g. to clear the selection). */
  onSuccess?: () => void;
};

function BulkMessageModal({
  open,
  onClose,
  campaignId,
  brandId,
  contractIds,
  creators,
  totalAcceptedCount,
  allLoadedSelected,
  onSuccess,
}: BulkMessageModalProps) {
  const bulkCreateMessages = useBulkCreateMessages();
  const [body, setBody] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const {
    pendingAttachments,
    isUploading,
    fileInputRef,
    handleAttachClick,
    handleFileChange,
    handleRemoveAttachment,
    reset: resetAttachments,
  } = useMessageAttachments();
  // Synchronous in-flight guard: `isLoading` only flips on the next render, so
  // a fast double-click could fire two sends before the button disables.
  const sendingRef = useRef(false);

  // Reset state whenever the modal is dismissed so the next open starts clean.
  useEffect(
    function resetState() {
      if (!open) {
        setBody('');
        setSendToAll(false);
        resetAttachments();
      }
    },
    [open, resetAttachments]
  );

  const selectedCount = contractIds.length;
  // Only offer "all accepted" when the user selected the entire loaded page and
  // there are more accepted creators than are currently loaded — otherwise the
  // explicit selection is exactly what they want.
  const canSendToAll = allLoadedSelected && totalAcceptedCount > selectedCount;
  const willSendToAll = canSendToAll && sendToAll;
  const effectiveCount = willSendToAll ? totalAcceptedCount : selectedCount;
  const isSingleRecipient = effectiveCount === 1;
  const isFormValid = body.trim().length > 0;
  const {isLoading} = bulkCreateMessages;

  const handleClose = () => {
    if (isLoading || isUploading) return;
    onClose();
  };

  const handleSend = async () => {
    if (sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    // Capture the recipient details before `onSuccess` clears the selection
    // upstream, so the success flash reports the correct count/name.
    const sentCount = effectiveCount;
    const singleName = creators[0]?.displayName ?? '';
    try {
      await bulkCreateMessages.mutateAsync({
        campaignId,
        brandId,
        // When messaging all accepted, the backend resolves the recipients from
        // the campaign itself; `contract_ids` then acts as an exclusion list
        // (empty here — we don't expose per-row exclusions in this flow).
        contractIds: willSendToAll ? [] : contractIds,
        sendToAll: willSendToAll,
        message: body.trim(),
        messageAttachments: pendingAttachments,
      });
      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: (
          <FlashMessage.Banner
            title={
              sentCount === 1
                ? translate('Message sent')
                : translate('Messages sent')
            }
            subtitle={
              sentCount === 1
                ? singleName
                : translate('{count} creators', {count: sentCount})
            }
          />
        ),
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      raiseFlashMessageError({
        title: translate('Failed to send messages'),
        subtitle: extractBackendErrorDetail(err),
      });
    } finally {
      sendingRef.current = false;
    }
  };

  const sendButtonLabel = (() => {
    if (isLoading) {
      return translate('Sending...');
    }
    if (isSingleRecipient) {
      return translate('Send');
    }
    return translate('Send to {count} creators', {count: effectiveCount});
  })();

  return (
    <Modal
      open={open}
      handleClose={isLoading ? undefined : handleClose}
      size="large"
      showCloseButton
      sx={BULK_MESSAGE_MODAL_SX}
    >
      <Stack gap="20px" alignItems="flex-start" sx={{width: '100%'}}>
        <StackedAvatars creators={creators} />
        <Headline size="md">
          {isSingleRecipient
            ? translate('Message {name}', {
                name: creators[0]?.displayName ?? '',
              })
            : translate('Message {count} creators', {count: effectiveCount})}
        </Headline>
        <Stack gap="8px" alignItems="flex-start" sx={{width: '100%'}}>
          <Label size="sm">{translate('Message')}</Label>
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder={translate(BODY_PLACEHOLDER_KEY)}
            variant="outlined"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isLoading}
            autoFocus
            inputProps={{'aria-label': translate('Message')}}
          />
          <MarkdownFormattingHint />
          <Button
            size="small"
            appearance="neutral"
            mode="plain"
            onClick={handleAttachClick}
            disabled={isLoading || isUploading}
            aria-label={translate('Attach file')}
          >
            <Icon size="small" appearance="neutral" name="Navigation-Attach" />
            {translate('Attach file')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MESSAGE_FILE_TYPES.join(',')}
            multiple
            onChange={handleFileChange}
            style={{display: 'none'}}
          />
          {isUploading ? (
            <Stack direction="row" gap="8px" alignItems="center">
              <CircularProgress size={12} />
              <Body size="xs">{translate('Uploading...')}</Body>
            </Stack>
          ) : null}
          {pendingAttachments.map((attachment) => (
            <Stack
              key={attachment.sourceId}
              direction="row"
              gap="8px"
              alignItems="center"
            >
              <Body size="xs">{attachment.fileName}</Body>
              <Button
                size="small"
                appearance="neutral"
                mode="plain"
                onClick={() => handleRemoveAttachment(attachment.sourceId)}
                disabled={isLoading}
                aria-label={translate('Remove attachment')}
              >
                <Icon
                  size="small"
                  appearance="neutral"
                  name="Actions-Close-Normal"
                />
              </Button>
            </Stack>
          ))}
        </Stack>
        {canSendToAll ? (
          <Stack gap="8px" alignItems="flex-start" sx={{width: '100%'}}>
            <CheckboxLabel
              checked={sendToAll}
              onChange={setSendToAll}
              disabled={isLoading}
              label={translate('Message all {count} accepted creators', {
                count: totalAcceptedCount,
              })}
            />
            {willSendToAll ? (
              <Body size="sm" color={designSystemToken('semantic.fg.warning')}>
                {translate(
                  'This messages all {count} accepted creators, including those not loaded on screen.',
                  {count: totalAcceptedCount}
                )}
              </Body>
            ) : null}
          </Stack>
        ) : null}
        <Stack direction="row" justifyContent="flex-end" sx={{width: '100%'}}>
          <Button
            mode="filled"
            appearance="neutral"
            size="large"
            onClick={handleSend}
            disabled={!isFormValid || isLoading || isUploading}
            isLoading={isLoading}
          >
            {sendButtonLabel}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

export default BulkMessageModal;
